/**
 * VAActionQueue — Iter-258
 *
 * Admin/VA workspace showing every completed activation that's still
 * waiting on post-event data (leads, engagements, samples, photos, recap).
 *
 * Closes the operational gap that was leaving Guardian Garage's Campaign
 * Intelligence dashboards stuck at 0.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ListChecks, Camera, BarChart3, FileText, Hash, Users } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';

const MISSING_LABELS = {
  leads:       { icon: Hash, label: 'Leads' },
  engagements: { icon: Users, label: 'Engagements' },
  samples:     { icon: BarChart3, label: 'Samples' },
  photos:      { icon: Camera, label: 'Photos' },
  recap:       { icon: FileText, label: 'Recap' },
};

export default function VAActionQueue() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/va-queue?limit=200');
        setItems(res.data?.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(-1)} data-testid="va-queue-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <ListChecks className="w-6 h-6 text-purple-600" />
                Capture Queue
              </h1>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-amber-700">Backup workflow.</span>{' '}
                Activations the talent did not recap within 24 hours of close. Talent remains the primary source — VAs only fill gaps when they don't.
              </p>
            </div>
          </div>
          <Badge className="bg-purple-100 text-purple-800 border-purple-200" data-testid="va-queue-pending-count">
            {items.length} pending
          </Badge>
        </div>

        {loading ? (
          <div className="text-gray-500 text-sm">Loading queue…</div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="text-emerald-700 font-semibold">All activations captured 🎉</div>
              <div className="text-gray-500 text-sm mt-1">Nothing pending. Great work, team.</div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {items.map((it) => (
              <Card key={it.shift_id} data-testid={`va-queue-row-${it.shift_id}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {it.event_title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {it.company_name || '—'}
                        {it.city ? ` · ${it.city}, ${it.state}` : ''}
                        {it.shift_date ? ` · ${new Date(it.shift_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/admin/shifts/${it.shift_id}`)}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid={`va-queue-capture-btn-${it.shift_id}`}
                    >
                      Capture
                    </Button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {Object.entries(it.missing).filter(([, v]) => v).map(([k]) => {
                      const { icon: Icon, label } = MISSING_LABELS[k] || {};
                      return (
                        <Badge
                          key={k}
                          className="bg-amber-50 text-amber-800 border-amber-200 inline-flex items-center gap-1"
                          data-testid={`va-queue-missing-${k}-${it.shift_id}`}
                        >
                          {Icon ? <Icon className="w-3 h-3" /> : null} {label || k}
                        </Badge>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
